import { EventType, PublicClientApplication } from '@azure/msal-browser';
import AyvensJsTheme from '@velocity/styling/themes/ayvens/theme';
import '@velocity/styling/themes/ayvens/theme.css';
import { VelocityProvider } from '@velocity/ui';
import ls from 'localstorage-slim';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { registerMsalAxiosInterceptor } from './api/msal-axios-interceptor';
import App from './app/app';
import {
  B2C_BRAND_SESSION_KEY,
  B2C_RECOVERY_ATTEMPT_SESSION_KEY,
  loginRequest,
  msalConfig,
} from './auth/msal-config';
import './index.css';
import { authenticate, setUserInformation } from './redux/auth/auth-slice';
import store from './redux/store';
import { routes } from './routes';
import { isAdb2cEnabled } from './utils/feature-flags';

// enable encryption globally
ls.config.encrypt = true;

// Development helper - expose localStorage decoder to window
if (import.meta.env.DEV) {
  (window as any).getAllDecryptedStorage = () => {
    const keys = Object.keys(localStorage);
    const values: Record<string, any> = {};
    keys.forEach((key: string) => {
      try {
        values[key] = ls.get(key, { decrypt: true });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        values[key] = 'Could not decrypt';
      }
    });
    return values;
  };
}

// Only construct with the real B2C config when the cookie is set.
// If Auth0 is active, use an inert safe config so MsalProvider has a valid
// instance without triggering any URL validation against unsubstituted placeholders.
export const msalInstance = isAdb2cEnabled()
  ? new PublicClientApplication(msalConfig)
  : new PublicClientApplication({
      auth: {
        clientId: '00000000-0000-0000-0000-000000000000',
        authority: 'https://login.microsoftonline.com/common',
      },
    });

const redirectToApplicationLogin = () => {
  store.dispatch(authenticate({ accessToken: '', expiresAt: 0, authId: '' }));
  store.dispatch(
    setUserInformation({
      firstName: '',
      lastName: '',
      emailVerified: false,
      email: '',
    }),
  );

  const brand = sessionStorage.getItem(B2C_BRAND_SESSION_KEY);
  const loginUrl = brand
    ? `${routes.login}?brand=${encodeURIComponent(brand)}`
    : routes.login;
  window.location.replace(loginUrl);
};

const redirectToAdb2cLogin = async () => {
  const brand = sessionStorage.getItem(B2C_BRAND_SESSION_KEY);
  try {
    await msalInstance.loginRedirect({
      ...loginRequest,
      extraQueryParameters: {
        ...loginRequest.extraQueryParameters,
        ...(brand && { brand }),
      },
    });
  } catch (error) {
    console.error('Failed to restart B2C login:', error);
    redirectToApplicationLogin();
  }
};

const recoverFromIncompleteB2cLogin = async () => {
  if (sessionStorage.getItem(B2C_RECOVERY_ATTEMPT_SESSION_KEY)) {
    redirectToApplicationLogin();
    return;
  }

  sessionStorage.setItem(B2C_RECOVERY_ATTEMPT_SESSION_KEY, 'true');
  await redirectToAdb2cLogin();
};

const renderApp = () => {
  console.log('Rendering React app');
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );
  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <VelocityProvider theme={AyvensJsTheme}>
          <App msalInstance={msalInstance} />
        </VelocityProvider>
      </BrowserRouter>
    </Provider>,
  );
};

if (isAdb2cEnabled()) {
  msalInstance.initialize().then(async () => {
    // Process B2C redirect response BEFORE rendering React
    let response;
    try {
      response = await msalInstance.handleRedirectPromise();
    } catch (error) {
      console.error('Failed to process B2C redirect response:', error);
      redirectToApplicationLogin();
      return;
    }

    if (response) {
      if (!response.accessToken || !response.account || !response.idTokenClaims) {
        await recoverFromIncompleteB2cLogin();
        return;
      }

      msalInstance.setActiveAccount(response.account);

      const claims = response.idTokenClaims as Record<string, unknown>;
      if (!claims.sub && !claims.oid) {
        await recoverFromIncompleteB2cLogin();
        return;
      }

      sessionStorage.removeItem(B2C_RECOVERY_ATTEMPT_SESSION_KEY);
      sessionStorage.removeItem(B2C_BRAND_SESSION_KEY);

      store.dispatch(
        authenticate({
          accessToken: response.accessToken,
          expiresAt: response.expiresOn ? response.expiresOn.getTime() : 0,
          authId: (claims.sub as string) || (claims.oid as string) || '',
        }),
      );
      store.dispatch(
        setUserInformation({
          firstName: (claims.given_name as string) || '',
          lastName: (claims.family_name as string) || '',
          emailVerified: true,
          email: (claims.username as string) || '',
        }),
      );
    } else if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
        event.payload
      ) {
        const payload = event.payload as {
          account: Parameters<typeof msalInstance.setActiveAccount>[0];
        };
        msalInstance.setActiveAccount(payload.account);
      }
    });

    // Register axios interceptor for automatic B2C token refresh
    registerMsalAxiosInterceptor(msalInstance);

    renderApp();
  });
} else {
  renderApp();
}
